import { useCallback } from "react";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";

export interface PrimaryControlsStripExpansionState {
  isExpanded: boolean;
  setExpanded: (expanded: boolean) => void;
  toggleExpanded: () => void;
}

/**
 * Reads the shared expand/collapse flag from the app context (which lives above the navigator)
 * and exposes setters that update it for every consumer. Storing the flag in app context — rather
 * than on the per-mount expansion registry — is what lets it survive navigation: a screen blurring
 * or unmounting no longer collapses the strip. The registry still owns published *content*; this
 * hook only owns whether the strip is open. Because the flag lives in shared context, the toggle
 * control and the rendered panel can live in separate subtrees — e.g. the toggle in the landscape
 * sidebar and the panel sliding down over the scene — and stay in sync.
 */
export function usePrimaryControlsStripExpansionState(): PrimaryControlsStripExpansionState {
  const [isExpanded, setPrimaryControlsStripExpanded, togglePrimaryControlsStripExpanded] =
    useContextSelectors(
      NavContext,
      (c) => c.isPrimaryControlsStripExpanded,
      (c) => c.setPrimaryControlsStripExpanded,
      (c) => c.togglePrimaryControlsStripExpanded,
    );

  const setExpanded = useCallback(
    (expanded: boolean) => {
      setPrimaryControlsStripExpanded(expanded);
    },
    [setPrimaryControlsStripExpanded],
  );

  const toggleExpanded = useCallback(() => {
    togglePrimaryControlsStripExpanded();
  }, [togglePrimaryControlsStripExpanded]);

  return { isExpanded, setExpanded, toggleExpanded };
}
